import React from 'react';
import { AssetViewer } from './AssetViewer';
import { VisualAssetData } from './types';
import { Button } from '@/components/ui/button';
import { Edit2 } from 'lucide-react';
import { useAsset, assetHelpers, Asset } from '@/hooks/useAsset';

interface AssetViewerWrapperProps {
  assetId: string;
  className?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  width?: string | number;
  height?: string | number;
  showEditButton?: boolean;
  onEdit?: (assetData: Asset) => void;
  marginPct?: number;
  fontScale?: 'extra-small' | 'small' | 'normal' | 'large';
  fillContainer?: boolean;
}

// Function to detect aspect ratio from asset data
const detectAspectRatio = (asset: Asset): string => {
  // Use saved aspect ratio if available
  if (asset.crop_aspect_ratio) {
    return asset.crop_aspect_ratio;
  }

  // For pricing pages, default to 16:9 for better display
  // This makes tall screenshots fill more of the container
  return '16:9';
};

// Simplified conversion from unified Asset to VisualAssetData
const convertAssetToVisualAssetData = (asset: Asset): VisualAssetData => {
  const primaryDisplayUrl = assetHelpers.getPrimaryDisplayUrl(asset);
  const secondaryDisplayUrl = assetHelpers.getSecondaryDisplayUrl(asset);

  // Determine chronological order for before/after display
  const primaryDate = asset.version ? parseInt(asset.version) : 0;
  const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;

  // If primary is newer than secondary, we need to swap for chronological display
  const shouldSwapForChronology = primaryDate > secondaryDate && secondaryDisplayUrl;


  const beforeImageUrl = shouldSwapForChronology ? secondaryDisplayUrl : primaryDisplayUrl;
  const afterImageUrl = shouldSwapForChronology ? primaryDisplayUrl : secondaryDisplayUrl;
  const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
  const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

  // Create image arrays that match the display order (before/after)
  const beforeImages = shouldSwapForChronology && secondaryDisplayUrl ? [{
    version: asset.secondary_version || asset.version,
    url: secondaryDisplayUrl
  }] : primaryDisplayUrl ? [{
    version: asset.version,
    url: primaryDisplayUrl
  }] : [];

  const afterImages = shouldSwapForChronology && primaryDisplayUrl ? [{
    version: asset.version,
    url: primaryDisplayUrl
  }] : secondaryDisplayUrl ? [{
    version: asset.secondary_version || asset.version,
    url: secondaryDisplayUrl
  }] : [];

  return {
    aspectRatio: detectAspectRatio(asset), // Intelligent aspect ratio detection
    viewMode: asset.comparison_mode || 'single',
    comparison_mode: asset.comparison_mode || 'single',
    header: asset.header || '',
    subheader: asset.subheader || '',
    companyName: asset.slug || '',
    companyLogo: asset.logo_url || asset.page?.company?.logo_url || '',
    primaryImage: beforeImageUrl,  // Show chronologically older image as primary
    secondaryImage: afterImageUrl, // Show chronologically newer image as secondary
    primaryImages: beforeImages,   // Array that matches what's shown as primary
    secondaryImages: afterImages,  // Array that matches what's shown as secondary
    selectedPrimaryVersion: beforeVersion || beforeImages[0]?.version,
    selectedSecondaryVersion: afterVersion || afterImages[0]?.version,
    initialState: {
      primary: asset.primary_viewport_state || undefined,
      secondary: asset.secondary_viewport_state || undefined
    }
  };
};

export const AssetViewerWrapper: React.FC<AssetViewerWrapperProps> = ({
  assetId,
  className,
  showHeader = true,
  showFooter = true,
  width,
  height,
  showEditButton = false,
  onEdit,
  marginPct = 0.025,
  fontScale = 'normal',
  fillContainer = false
}) => {
  // Use unified asset hook - single source of truth
  const { asset, isLoading, isError, error } = useAsset(assetId);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height
      }}>
        <div className="text-muted-foreground">Loading asset...</div>
      </div>
    );
  }

  if (isError || !asset) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height
      }}>
        <div className="text-red-500">
          {error?.message || 'Failed to load asset'}
        </div>
      </div>
    );
  }

  // Convert to VisualAssetData format for AssetViewer
  const visualAssetData = convertAssetToVisualAssetData(asset);

  return (
    <div className="w-full h-full relative">{/* Debug panels removed for cleaner collections display */}

      {/* Edit Button - Floating in top right */}
      {showEditButton && onEdit && (
        <div className="absolute top-1 right-1 z-40">
          <Button
            variant="default"
            size="sm"
            onClick={() => onEdit(asset)}
            className="flex items-center gap-2 shadow-lg"
          >
            <Edit2 className="h-4 w-4" />
            Edit Asset
          </Button>
        </div>
      )}

      {/* Asset Viewer - Full Layout */}
      <AssetViewer
        data={visualAssetData}
        className={className}
        showHeader={showHeader}
        showFooter={showFooter}
        width={width}
        height={height}
        marginPct={marginPct}
        fontScale={fontScale}
        fillContainer={fillContainer}
      />
    </div>
  );
};